
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSchool } from "../../../context/SchoolContext";
import { staffApi } from "../../staff/services/staffApi";
import {
  Users,
  DollarSign,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  UserPlus,
  CreditCard,
  BadgeInfo,
  CalendarOff,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();

  const [summary, setSummary] = useState({
    total_students: 0,
    fees_collected: 0,
    pending_fees: 0,
    attendance_percentage: 0,
  });

  const [monthlyFeeCollection, setMonthlyFeeCollection] = useState([]);

  const [monthlyAttendance, setMonthlyAttendance] = useState([]);


  const [recentActivity, setRecentActivity] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!schoolId || !activeAcademicYearId) return;

      setLoading(true);

      try {
        const response = await staffApi.getDashboardSummary(schoolId, {
          academicYearId: activeAcademicYearId,
        });

        if (response?.success && response?.data) {
          setSummary(response.data.summary);

          setMonthlyFeeCollection(response.data.monthly_fee_collection || []);

          setMonthlyAttendance(response.data.monthly_attendance || []);

          setRecentActivity(response.data.recent_activity || []);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard summary", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [schoolId, activeAcademicYearId]);

  console.log("Monthly Fee Collection", monthlyFeeCollection);

  console.log("Monthly Attendance", monthlyAttendance);

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Dashboard Overview
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Welcome back, here's what's happening at your school today.
        </p>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
            <Users className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">
            Total Students
          </p>

          <p className="text-2xl font-bold text-gray-900">
            {loading ? "--" : summary.total_students}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center mb-4">
            <DollarSign className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">
            Fees Collected (₹)
          </p>

          <p className="text-2xl font-bold text-gray-900">
            ₹{loading ? "--" : Number(summary.fees_collected).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-500 flex items-center justify-center mb-4">
            <AlertCircle className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Pending Fees</p>

          <p className="text-2xl font-bold text-gray-900">
            ₹{loading ? "--" : Number(summary.pending_fees).toLocaleString()}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center mb-4">
            <TrendingUp className="w-5 h-5" />
          </div>
          <p className="text-sm font-medium text-gray-500 mb-1">Attendance %</p>

          <p className="text-2xl font-bold text-gray-900">
            {loading ? "--" : `${summary.attendance_percentage}%`}
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Line Chart Mockup */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Monthly Fees Collected
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Total collected ₹
                {loading
                  ? "--"
                  : Number(summary.fees_collected).toLocaleString()}{" "}
                this year
              </p>
            </div>

            <span className="text-sm text-gray-400">
              {loading ? "--" : activeAcademicYear?.year_name || "--"}
            </span>
          </div>

          <div className="h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={monthlyFeeCollection}
                margin={{
                  top: 10,
                  right: 10,
                  left: -10,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e5e7eb"
                />

                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />

                <YAxis axisLine={false} tickLine={false} width={28} />

                <Tooltip
                  formatter={(value) => [
                    `₹${Number(value).toLocaleString()}`,
                    "Collected",
                  ]}
                />

                <Line
                  type="monotone"
                  dataKey="collected"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart Mockup */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Monthly Attendance %
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Average {loading ? "--" : `${summary.attendance_percentage}%`}{" "}
                across all classes
              </p>
            </div>

            <span className="text-sm text-gray-400">
              {loading ? "--" : activeAcademicYear?.year_name || "--"}
            </span>
          </div>

          <div className="h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyAttendance}
                margin={{
                  top: 10,
                  right: 10,
                  left: -10,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#e5e7eb"
                />

                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />

                {/* <YAxis /> */}
                <YAxis axisLine={false} tickLine={false} width={28} />

                <Tooltip formatter={(value) => [`${value}%`, "Attendance"]} />

                <Bar
                  dataKey="percentage"
                  fill="#3b82f6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-base font-bold text-gray-900">
                Recent Activity
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Latest student updates and transactions
              </p>
            </div>
            <a
              href="#"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center"
            >
              View all <ArrowRight className="w-4 h-4 ml-1" />
            </a>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead>
                <tr className="border-b border-gray-100 text-gray-400">
                  <th className="pb-3 font-medium">Student Name</th>
                  <th className="pb-3 font-medium">Action</th>
                  <th className="pb-3 font-medium">Date</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {recentActivity.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50/50">
                    <td className="py-3 font-medium text-gray-900">
                      {row.student_name}
                    </td>

                    <td className="py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
          ${row.action === "Added"
                            ? "text-blue-700 bg-blue-100"
                            : row.action === "Updated"
                              ? "text-orange-700 bg-orange-100"
                              : row.action === "Fee Paid"
                                ? "text-green-700 bg-green-100"
                                : "text-gray-700 bg-gray-100"
                          }`}
                      >
                        {row.action}
                      </span>
                    </td>

                    <td className="py-3">
                      {new Date(row.date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="mb-6">
            <h2 className="text-base font-bold text-gray-900">Quick Actions</h2>
            <p className="text-sm text-gray-500 mt-1">Frequently used tasks</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => navigate("/students")}
              className="flex flex-col items-center justify-center p-4 bg-blue-600 hover:bg-blue-700 transition-colors text-white rounded-xl shadow-sm h-28"
            >
              <UserPlus className="w-7 h-7 mb-2" />
              <span className="text-sm font-medium">Add Students</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/fees/collection")}
              className="flex flex-col items-center justify-center p-4 bg-[#00b341] hover:bg-green-600 transition-colors text-white rounded-xl shadow-sm h-28"
            >
              <CreditCard className="w-7 h-7 mb-2" />
              <span className="text-sm font-medium">Collect Fees</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/documents/id-cards")}
              className="flex flex-col items-center justify-center p-4 bg-purple-600 hover:bg-purple-700 transition-colors text-white rounded-xl shadow-sm h-28"
            >
              <BadgeInfo className="w-7 h-7 mb-2" />
              <span className="text-sm font-medium">Generate ID</span>
            </button>
            <button className="flex flex-col items-center justify-center p-4 bg-[#e60000] hover:bg-red-700 transition-colors text-white rounded-xl shadow-sm h-28">
              <CalendarOff className="w-7 h-7 mb-2" />
              <span className="text-sm font-medium">Mark Attend.</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
